import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import artifactContentCopy from '@/i18n/ns/artifact-content';
import type { NumberFormat } from '../../../../src/artifacts/presentation-values.mjs';
import type { DocumentBlock, TextRun, ValueSlot } from '../models.ts';
import type {
  SavedDisplayBlock,
  SavedDisplayRun,
} from '../../../../src/artifacts/saved-view-contract.mjs';
import type { Scalar } from '../models.ts';
import { formatScalar } from '../format.ts';
import { projectNullDisplay } from '../../../../src/artifacts/presentation/null-display.mjs';
import { presentationStyle } from '../presentation/style.ts';
import { useNarrowViewport } from '@/lib/mobile';
import { mediaCopy } from '@/i18n/ns/media';
import {
  fromManagedImage,
  MediaViewer,
  pickSize,
  shownState,
  MEDIA_PLACEHOLDER_GLYPH,
} from '../../media';
import { cssRatio, STATE_COPY } from '../../media/model';
import { TableView } from '../table/table-view';
import { SavedChartDisplayView } from '../chart/saved-chart-view';
import { ManagedImage } from './managed-image';

// The shared blocks of a document and of a report section. Every node carries
// `data-inspect-node` with its canonical id: a lookup hint for the inspector,
// never authority, and never an array index or visible text. A bound value
// shows what was observed; where it came from is the Sources panel's to show.

export type ViewerProps = { className?: string; style?: CSSProperties; locale?: string };

export function ValueView({
  slot,
  locale = 'en',
  format,
}: {
  slot: ValueSlot;
  locale?: string;
  format?: NumberFormat;
}) {
  return (
    <ScalarView
      scalar={slot.kind === 'literal' ? slot.value : slot.observed}
      locale={locale}
      format={format}
    />
  );
}

// What a saved view carries instead of a slot: the authorized scalar itself, with no
// binding or evidence handle. Both paths show the value the same way.
export function ScalarView({
  scalar,
  locale = 'en',
  format,
}: {
  scalar: Scalar;
  locale?: string;
  format?: NumberFormat;
}) {
  const copy = useNamespaceCopy(artifactContentCopy);
  const shown = formatScalar(scalar, locale, format);
  if (shown.kind === 'text') return <span className="ws-value">{shown.text}</span>;
  const label =
    shown.reason === 'redacted'
      ? copy.valueRedacted
      : shown.reason === 'source-null'
        ? copy.valueSourceNull
        : copy.valueMissing;
  // A null is its own state, never 0: a typed format's nullStyle picks the dash, nothing
  // or the written reason, and the reason stays its accessible name in every case.
  const glyph =
    format && scalar.kind === 'null'
      ? projectNullDisplay(scalar, { locale, surface: 'document', nullStyle: format.nullStyle })
          .text
      : '—';
  return (
    <span className="ws-value ws-value-null" data-null={shown.reason} role="img" aria-label={label}>
      <span aria-hidden="true">{glyph}</span>
    </span>
  );
}

export function RunView({ run, locale }: { run: TextRun | SavedDisplayRun; locale?: string }) {
  let node: ReactNode =
    run.kind === 'text' ? (
      run.text
    ) : 'slot' in run ? (
      <ValueView slot={run.slot} locale={locale} format={run.presentation?.numberFormat} />
    ) : (
      <ScalarView scalar={run.value} locale={locale} format={run.presentation?.numberFormat} />
    );
  if (run.marks.includes('underline')) node = <u>{node}</u>;
  if (run.marks.includes('italic')) node = <em>{node}</em>;
  if (run.marks.includes('bold')) node = <strong>{node}</strong>;
  return (
    <span
      data-inspect-node={run.id}
      data-run={run.kind}
      style={presentationStyle(run.presentation, 'paper')}
    >
      {node}
    </span>
  );
}

const Runs = ({ runs, locale }: { runs: (TextRun | SavedDisplayRun)[]; locale?: string }) => (
  <>
    {runs.map((run) => (
      <RunView key={run.id} run={run} locale={locale} />
    ))}
  </>
);

export type RegisteredRender = (ref: string, kind: 'table' | 'chart') => ReactNode;
/**
 * What a document says about a table or chart it embeds when it does not render it: the
 * referenced Artifact's title and, for a table, its row count, from ART's projection.
 */
export type RegisteredDescribe = (
  ref: string,
  kind: 'table' | 'chart',
) => { title: string; rows?: number } | null;

export function BlockView({
  block,
  locale,
  renderRegistered,
  describeRegistered,
  onOpenRegistered,
  imageSrc,
}: {
  block: DocumentBlock | SavedDisplayBlock;
  locale?: string;
  renderRegistered?: RegisteredRender;
  describeRegistered?: RegisteredDescribe;
  /** Opens the embedded table or chart where it lives (the sheet or chart viewer). */
  onOpenRegistered?: (ref: string, kind: 'table' | 'chart') => void;
  /**
   * The authorized source of a governed image (a data or asset URL ART resolved for this
   * audience), or null. Without this prop the block shows its alt text only (the viewer's
   * explicit limitation until the authorized image adapter is wired); print supplies it.
   */
  imageSrc?: (imageId: string, nodeId?: string) => string | null;
}) {
  const copy = useNamespaceCopy(artifactContentCopy);
  const common = {
    'data-inspect-node': block.id,
    // A table or chart is its registered payload's: no geometry is invented for it here.
    style: 'presentation' in block ? presentationStyle(block.presentation, 'paper') : undefined,
  };
  switch (block.kind) {
    case 'paragraph':
      return (
        <p {...common}>
          <Runs runs={block.runs} locale={locale} />
        </p>
      );
    case 'heading': {
      const Tag = block.level === 1 ? 'h2' : block.level === 2 ? 'h3' : 'h4';
      return (
        <Tag {...common}>
          <Runs runs={block.runs} locale={locale} />
        </Tag>
      );
    }
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag {...common}>
          {block.items.map((item) => (
            <li key={item.id} data-inspect-node={item.id}>
              <Runs runs={item.runs} locale={locale} />
            </li>
          ))}
        </Tag>
      );
    }
    case 'table':
    case 'chart': {
      const kind = block.kind;
      if ('display' in block)
        return (
          <div {...common} className="ws-doc-registered" data-ref={block.registeredPayloadRef}>
            {block.kind === 'table' ? (
              <TableView display={block.display} locale={locale} embedded />
            ) : (
              <SavedChartDisplayView display={block.display} locale={locale} />
            )}
          </div>
        );
      const rendered = renderRegistered?.(block.registeredPayloadRef, kind);
      if (rendered)
        return (
          <div {...common} className="ws-doc-registered" data-ref={block.registeredPayloadRef}>
            {rendered}
          </div>
        );
      // Not rendered here: say what it is, never an empty labelled box.
      const info = describeRegistered?.(block.registeredPayloadRef, kind) ?? null;
      const label = kind === 'table' ? copy.tablePlaceholder : copy.chartPlaceholder;
      const described = info
        ? formatCopy(kind === 'table' ? copy.tableDescribed : copy.chartDescribed, {
            title: info.title,
          })
        : label;
      return (
        <p {...common} className="ws-doc-registered" data-ref={block.registeredPayloadRef}>
          {described}
          {info?.rows !== undefined && ` (${formatCopy(copy.tableRows, { count: info.rows })})`}
          {onOpenRegistered && (
            <button
              type="button"
              className="open"
              onClick={() => onOpenRegistered(block.registeredPayloadRef, kind)}
            >
              {kind === 'table' ? copy.openInSheet : copy.openChart}
            </button>
          )}
        </p>
      );
    }
    case 'image':
      if ('ref' in block) return <ManagedImageBlock block={block} locale={locale} />;
      return <ImageBlock block={block} imageSrc={imageSrc} />;
  }
}

/** Saved images carry only the current public projection, never a source resolver. */
function ManagedImageBlock({
  block,
  locale,
}: {
  block: Extract<SavedDisplayBlock, { kind: 'image' }>;
  locale?: string;
}) {
  const copy = useNamespaceCopy(mediaCopy);
  const phone = useNarrowViewport();
  const origin = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const item = fromManagedImage(block.ref, { key: block.id, ratio: block.ratio });
  const state = shownState(item);
  const size = state === 'ready' ? pickSize(item, 640) : null;
  const ready = size !== null && failedSource !== size.src;
  const placeholder = ready ? null : copy[STATE_COPY[state === 'ready' ? 'refused' : state]];
  const aspectRatio = cssRatio(item.ratio) ?? undefined;
  return (
    <figure
      data-inspect-node={block.id}
      className="ws-doc-image"
      style={presentationStyle(block.presentation, 'paper')}
    >
      {ready && size ? (
        <button
          ref={origin}
          type="button"
          aria-label={item.alt || copy.viewer}
          style={{
            display: 'block',
            width: '100%',
            padding: 0,
            border: 0,
            background: 'transparent',
            aspectRatio,
          }}
          onClick={() => setOpen(true)}
        >
          <ManagedImage
            src={size.src}
            alt={item.alt}
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: block.fit === 'cover' && block.cropSafe ? 'cover' : 'contain',
            }}
            onError={() => setFailedSource(size.src)}
          />
        </button>
      ) : (
        <div
          className="pc-media-placeholder"
          data-state={state === 'ready' ? 'refused' : state}
          role="img"
          aria-label={item.alt ? `${item.alt} · ${placeholder}` : (placeholder ?? '')}
          style={{ width: '100%', aspectRatio }}
        >
          <svg
            className="pc-media-glyph"
            viewBox={MEDIA_PLACEHOLDER_GLYPH.viewBox}
            aria-hidden="true"
          >
            <rect {...MEDIA_PLACEHOLDER_GLYPH.rect} />
            <circle {...MEDIA_PLACEHOLDER_GLYPH.circle} />
            <path d={MEDIA_PLACEHOLDER_GLYPH.path} />
          </svg>
          <span aria-hidden="true">{placeholder}</span>
        </div>
      )}
      {block.ref.state !== 'refused' && block.caption !== undefined && (
        <figcaption>{block.caption}</figcaption>
      )}
      {open && (
        <MediaViewer
          items={[item]}
          index={0}
          onIndexChange={() => {}}
          onClose={() => setOpen(false)}
          origin={() => origin.current}
          phone={phone}
          locale={locale}
          imageComponent={ManagedImage}
        />
      )}
    </figure>
  );
}

// An image is a governed asset id, never a URL the viewer builds. Without an authorized
// source (the viewer's explicit limitation until the image adapter is wired) the block
// shows its alt text; print supplies the source, or the export is refused.
function ImageBlock({
  block,
  imageSrc,
}: {
  block: Extract<DocumentBlock, { kind: 'image' }>;
  imageSrc?: (imageId: string, nodeId?: string) => string | null;
}) {
  const copy = useNamespaceCopy(artifactContentCopy);
  // The retained legacy viewer keeps its authorized adapter. New managed refs
  // use the current canonical image projection, mounted by the shared host.
  const imageId = block.media?.state === 'ready' ? block.media.imageId : block.imageId;
  const src = imageId === undefined ? null : (imageSrc?.(imageId, block.id) ?? null);
  return (
    <figure
      data-inspect-node={block.id}
      className="ws-doc-image"
      style={presentationStyle(block.presentation, 'paper')}
    >
      {src ? (
        <img src={src} alt={block.alt} />
      ) : (
        <figcaption>{block.alt || copy.imageUnavailable}</figcaption>
      )}
      {src && block.caption !== undefined && <figcaption>{block.caption}</figcaption>}
    </figure>
  );
}
